import { useEffect, useMemo, useRef, useState } from "react";

/**
 * How long a row takes to close, and how long a page takes to hand over to Home
 * when the task it was showing is archived.
 *
 * The pair mirrors `RivetsMotion.layout` (100ms) and `RivetsMotion.pageHandover`
 * (160ms) on the native clients, and `--row-close` / `--page-handover` in
 * `global.css`. Anything that removes a row has to wait exactly as long as the
 * stylesheet spends closing it.
 */
export const ROW_CLOSE_MS = 100;
export const ROW_OPEN_MS = 100;
export const PAGE_HANDOVER_MS = 160;

/** A row that has left the list, and the index it left from. */
export interface ClosingRow<T> {
  item: T;
  index: number;
}

/**
 * The rows in `previous` that `present` no longer has and that are not already
 * closing. A row already on its way out is left alone: restarting its animation
 * every render would hold it on screen forever.
 */
export function departedRows<T>(
  previous: readonly T[],
  present: ReadonlySet<string>,
  closing: ReadonlySet<string>,
  keyOf: (item: T) => string,
): Array<ClosingRow<T>> {
  return previous
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => !present.has(keyOf(item)) && !closing.has(keyOf(item)));
}

/**
 * `items` with each closing row put back at the index it left from, so it
 * closes between the neighbours it had rather than at the end of the list.
 *
 * Reinserted in ascending index order: taken the other way round, each row
 * would push the one placed before it along by one.
 */
export function mergeClosingRows<T>(items: readonly T[], closing: ReadonlyArray<ClosingRow<T>>): T[] {
  const rows = [...items];
  for (const { item, index } of [...closing].sort((a, b) => a.index - b.index)) {
    rows.splice(Math.min(index, rows.length), 0, item);
  }
  return rows;
}

/**
 * Names the rows that are arriving in a list and keeps the ones that are
 * leaving it mounted for the length of their closing animation.
 *
 * Archiving a task takes it out of every list it was in. React drops the node
 * the moment the query data changes, so the row blinked out and the rows under
 * it jumped up — the jank FRNT-753 is about. This puts the departed row back
 * where it was and names it in `closing`, so the caller can hand it the class
 * that shuts its height; once the animation is over it is dropped for real.
 *
 * `rows` is the input array itself whenever nothing is closing, so the common
 * case costs nothing downstream: memos and query lists keyed on it do not see a
 * new identity on every render.
 *
 * `opening` is the mirror: a task that has just been created — here, on another
 * device, or by an automation — opens into the list rather than appearing in
 * it. The first render is not an arrival, so a cold load does not animate every
 * row it draws.
 *
 * `keyOf` must be stable — a module-level function, not an inline arrow. It is
 * deliberately not a dependency of the effect below, because a new function
 * every render would read as a new list every render.
 */
export function useRowTransitions<T>(
  items: T[],
  keyOf: (item: T) => string,
  ms: number = ROW_CLOSE_MS,
): { rows: T[]; closing: ReadonlySet<string>; opening: ReadonlySet<string> } {
  const previous = useRef(items);
  const [closing, setClosing] = useState<Array<ClosingRow<T>>>([]);
  const [opening, setOpening] = useState<ReadonlySet<string>>(() => new Set());
  const known = useRef<Set<string> | null>(null);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const openTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  useEffect(() => {
    const present = new Set(items.map(keyOf));

    // Arrivals. The first pass only records what was already on screen — rows
    // drawn by the initial render are not arriving, they are simply there.
    if (known.current === null) {
      known.current = present;
    } else {
      const arrived = [...present].filter((key) => !known.current!.has(key));
      known.current = present;
      if (arrived.length > 0) {
        setOpening((current) => new Set([...current, ...arrived]));
        for (const key of arrived) {
          clearTimeout(openTimers.current.get(key));
          openTimers.current.set(key, setTimeout(() => {
            openTimers.current.delete(key);
            setOpening((current) => {
              const next = new Set(current);
              next.delete(key);
              return next;
            });
          }, ROW_OPEN_MS));
        }
      }
    }

    // A row that came back before its timer ran out — Undo on the archive
    // toast — is live again, and must not be dropped a second time.
    for (const [key, timer] of [...timers.current]) {
      if (!present.has(key)) continue;
      clearTimeout(timer);
      timers.current.delete(key);
      setClosing((current) => current.filter((row) => keyOf(row.item) !== key));
    }
    const departed = departedRows(previous.current, present, new Set(timers.current.keys()), keyOf);
    previous.current = items;
    if (departed.length === 0) return;
    setClosing((current) => [...current, ...departed]);
    for (const { item } of departed) {
      const key = keyOf(item);
      timers.current.set(key, setTimeout(() => {
        timers.current.delete(key);
        setClosing((current) => current.filter((row) => keyOf(row.item) !== key));
      }, ms));
    }
  }, [items, ms]);

  useEffect(() => {
    const pendingClose = timers.current;
    const pendingOpen = openTimers.current;
    return () => {
      for (const timer of pendingClose.values()) clearTimeout(timer);
      for (const timer of pendingOpen.values()) clearTimeout(timer);
      pendingClose.clear();
      pendingOpen.clear();
    };
  }, []);

  return useMemo(() => {
    if (closing.length === 0) return { rows: items, closing: EMPTY, opening };
    return {
      rows: mergeClosingRows(items, closing),
      closing: new Set(closing.map((row) => keyOf(row.item))),
      opening,
    };
  }, [closing, items, opening]);
}

const EMPTY: ReadonlySet<string> = new Set();
